import i18n from "i18next";
import { useState, useCallback, useMemo } from "react";
import {
  IconCheck,
  IconTrash,
  IconCornerDownRight,
  IconSend,
  IconCircleCheck,
  IconCircleDashed,
  IconX,
} from "@tabler/icons-react";
import {
  useComments,
  useCommentCount,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useCreateReply,
  useDeleteReply,
  type Comment,
} from "@/hooks/useComments";
// Minimal block shape — compatible with both Block and CardBlock
interface BlockLike {
  id: string;
  type: string;
  content: Record<string, unknown>;
}

// --- Time ago utility ---

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return i18n.t("editor:comments.justNow");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return i18n.t("editor:comments.minAgo", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return i18n.t("editor:comments.hAgo", { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return i18n.t("editor:comments.dAgo", { n: days });
  const months = Math.floor(days / 30);
  return i18n.t("editor:comments.moAgo", { n: months });
}

// --- Sub-components ---

type FilterTab = "all" | "unresolved" | "resolved";

interface CommentsPanelProps {
  documentId: string;
  blocks?: BlockLike[];
  onScrollToBlock?: (blockId: string) => void;
}

function ReplyForm({
  docId,
  commentId,
  onClose,
}: {
  docId: string;
  commentId: string;
  onClose: () => void;
}) {
  const [content, setContent] = useState("");
  const createReply = useCreateReply(docId, commentId);

  const handleSubmit = useCallback(() => {
    const trimmed = content.trim();
    if (!trimmed) return;
    createReply.mutate(
      { content: trimmed },
      {
        onSuccess: () => {
          setContent("");
          onClose();
        },
      }
    );
  }, [content, createReply, onClose]);

  return (
    <div className="mt-2 flex gap-1.5">
      <input
        type="text"
        data-testid="comment-reply-input"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
          if (e.key === "Escape") onClose();
        }}
        placeholder={i18n.t("editor:comments.writeAReply")}
        className="flex-1 px-2.5 py-1.5 text-xs rounded-md border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-bg,#fff)] text-[var(--lilia-text,#334155)] placeholder:text-[var(--lilia-text-muted,#94a3b8)] focus:outline-none focus:ring-1 focus:ring-[var(--lilia-primary,#0A76A4)]"
        autoFocus
      />
      <button
        data-testid="comment-reply-send"
        onClick={handleSubmit}
        disabled={!content.trim() || createReply.isPending}
        className="p-1.5 rounded-md bg-[var(--lilia-primary,#0A76A4)] text-[color:var(--lilia-on-primary)] disabled:opacity-40 hover:opacity-90 transition-opacity"
        title={i18n.t("editor:comments.sendReply")}
      >
        <IconSend size={14} />
      </button>
      <button
        data-testid="comment-reply-cancel"
        onClick={onClose}
        className="p-1.5 rounded-md text-[var(--lilia-text-muted,#94a3b8)] hover:text-[var(--lilia-text,#334155)] hover:bg-[var(--lilia-surface,#f1f5f9)] transition-colors"
        title={i18n.t("editor:comments.cancel")}
      >
        <IconX size={14} />
      </button>
    </div>
  );
}

function CommentCard({
  comment,
  docId,
  blockLabel,
  onScrollToBlock,
}: {
  comment: Comment;
  docId: string;
  blockLabel: string | null;
  onScrollToBlock?: (blockId: string) => void;
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const updateComment = useUpdateComment(docId);
  const deleteComment = useDeleteComment(docId);
  const deleteReply = useDeleteReply(docId, comment.id);

  const handleToggleResolved = useCallback(() => {
    updateComment.mutate({
      commentId: comment.id,
      data: { resolved: !comment.resolved },
    });
  }, [comment, updateComment]);

  const handleDelete = useCallback(() => {
    if (!confirm(i18n.t("editor:comments.deleteThisComment"))) return;
    deleteComment.mutate(comment.id);
  }, [comment.id, deleteComment]);

  const handleDeleteReply = useCallback(
    (replyId: string) => {
      if (!confirm(i18n.t("editor:comments.deleteThisReply"))) return;
      deleteReply.mutate(replyId);
    },
    [deleteReply]
  );

  return (
    <div
      data-testid="comment-card"
      data-comment-id={comment.id}
      data-resolved={comment.resolved ? "true" : "false"}
      className={`rounded-lg border transition-colors ${
        comment.resolved
          ? "border-green-200 bg-green-50/40 dark:border-green-900 dark:bg-green-950/20"
          : "border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-bg,#fff)]"
      }`}
    >
      {/* Header */}
      <div className="px-3 pt-2.5 pb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {blockLabel ? (
            <button
              data-testid="comment-block-link"
              data-block-id={comment.blockId ?? undefined}
              onClick={() =>
                comment.blockId && onScrollToBlock?.(comment.blockId)
              }
              className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--lilia-surface,#f1f5f9)] text-[var(--lilia-primary,#0A76A4)] hover:bg-[var(--lilia-primary,#0A76A4)] hover:text-[color:var(--lilia-on-primary)] transition-colors truncate max-w-[120px]"
              title={`Go to block: ${blockLabel}`}
            >
              {blockLabel}
            </button>
          ) : (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--lilia-surface,#f1f5f9)] text-[var(--lilia-text-muted,#94a3b8)]">
              {i18n.t("editor:comments.general")}
            </span>
          )}
          {comment.resolved && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              {i18n.t("editor:comments.resolved")}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-3 pb-2">
        <div className="flex items-baseline gap-1.5 mb-1">
          <span className="text-xs font-semibold text-[var(--lilia-text,#334155)]">
            {comment.userName || "Unknown"}
          </span>
          <span className="text-[10px] text-[var(--lilia-text-muted,#94a3b8)]">
            {timeAgo(comment.createdAt)}
          </span>
        </div>
        <p className="text-xs text-[var(--lilia-text,#334155)] leading-relaxed whitespace-pre-wrap break-words">
          {comment.content}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-1 mt-2">
          <button
            data-testid="comment-reply-button"
            onClick={() => setShowReplyForm(true)}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-[var(--lilia-text-muted,#94a3b8)] hover:text-[var(--lilia-text,#334155)] hover:bg-[var(--lilia-surface,#f1f5f9)] transition-colors"
          >
            <IconCornerDownRight size={12} />
            {i18n.t("editor:comments.reply")}
          </button>
          <button
            data-testid="comment-resolve-button"
            onClick={handleToggleResolved}
            disabled={updateComment.isPending}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
              comment.resolved
                ? "text-green-600 hover:text-orange-600 hover:bg-orange-50"
                : "text-[var(--lilia-text-muted,#94a3b8)] hover:text-green-600 hover:bg-green-50"
            }`}
          >
            {comment.resolved ? (
              <>
                <IconCircleCheck size={12} />
                {i18n.t("editor:comments.unresolve")}
              </>
            ) : (
              <>
                <IconCheck size={12} />
                {i18n.t("editor:comments.resolve")}
              </>
            )}
          </button>
          <button
            data-testid="comment-delete-button"
            onClick={handleDelete}
            disabled={deleteComment.isPending}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-[var(--lilia-text-muted,#94a3b8)] hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <IconTrash size={12} />
            {i18n.t("editor:comments.delete")}
          </button>
        </div>

        {/* Reply form */}
        {showReplyForm && (
          <ReplyForm
            docId={docId}
            commentId={comment.id}
            onClose={() => setShowReplyForm(false)}
          />
        )}
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="border-t border-[var(--lilia-border,#e2e8f0)]">
          {comment.replies.map((reply) => (
            <div
              key={reply.id}
              data-testid="comment-reply"
              data-reply-id={reply.id}
              className="px-3 py-2 ml-4 border-l-2 border-[var(--lilia-border,#e2e8f0)]"
            >
              <div className="flex items-baseline gap-1.5 mb-0.5">
                <IconCornerDownRight
                  size={10}
                  className="text-[var(--lilia-text-muted,#94a3b8)] shrink-0 relative top-[1px]"
                />
                <span className="text-xs font-semibold text-[var(--lilia-text,#334155)]">
                  {reply.userName || "Unknown"}
                </span>
                <span className="text-[10px] text-[var(--lilia-text-muted,#94a3b8)]">
                  {timeAgo(reply.createdAt)}
                </span>
              </div>
              <p className="text-xs text-[var(--lilia-text,#334155)] leading-relaxed whitespace-pre-wrap break-words ml-4">
                {reply.content}
              </p>
              <button
                data-testid="comment-reply-delete"
                data-reply-id={reply.id}
                onClick={() => handleDeleteReply(reply.id)}
                className="ml-4 mt-1 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-[var(--lilia-text-muted,#94a3b8)] hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <IconTrash size={10} />
                {i18n.t("editor:comments.delete")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main panel ---

export function CommentsPanel({
  documentId,
  blocks,
  onScrollToBlock,
}: CommentsPanelProps) {
  const [filterTab, setFilterTab] = useState<FilterTab>("all");
  const [newContent, setNewContent] = useState("");
  const [newBlockId, setNewBlockId] = useState<string>("");

  // Derive query params from filter tab
  const queryParams = useMemo(() => {
    if (filterTab === "resolved") return { resolved: true };
    if (filterTab === "unresolved") return { resolved: false };
    return undefined;
  }, [filterTab]);

  const { data: comments, isLoading } = useComments(documentId, queryParams);
  const { data: counts } = useCommentCount(documentId);
  const createComment = useCreateComment(documentId);

  // Block label map for display
  const blockLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!blocks) return map;
    for (const block of blocks) {
      const content = block.content as Record<string, unknown>;
      let label = block.type.charAt(0).toUpperCase() + block.type.slice(1);
      if (block.type === "heading" && typeof content.text === "string") {
        label = content.text.slice(0, 40) || "Heading";
      } else if (
        block.type === "paragraph" &&
        typeof content.text === "string"
      ) {
        label = content.text.slice(0, 40) || "Paragraph";
      }
      map.set(block.id, label);
    }
    return map;
  }, [blocks]);

  const handleSubmit = useCallback(() => {
    const trimmed = newContent.trim();
    if (!trimmed) return;
    createComment.mutate(
      {
        content: trimmed,
        blockId: newBlockId || undefined,
      },
      {
        onSuccess: () => {
          setNewContent("");
          setNewBlockId("");
        },
      }
    );
  }, [newContent, newBlockId, createComment]);

  const handleScrollToBlock = useCallback(
    (blockId: string) => {
      if (onScrollToBlock) {
        onScrollToBlock(blockId);
      } else {
        // Fallback: dispatch a custom event that both Studio and Flow modes can listen for
        const el =
          document.querySelector(`[data-block-id="${blockId}"]`) ||
          document.querySelector(`[data-block-card="${blockId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    },
    [onScrollToBlock]
  );

  const totalCount = counts?.total ?? 0;

  return (
    <div className="flex flex-col h-full" data-testid="comments-panel">
      {/* Header */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[var(--lilia-text,#334155)]">
            {i18n.t("editor:comments.comments")}
            {totalCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[var(--lilia-primary,#0A76A4)] text-[color:var(--lilia-on-primary)] text-[10px] font-bold leading-none">
                {totalCount}
              </span>
            )}
          </h3>
          {counts && counts.unresolved > 0 && (
            <span className="text-[10px] text-[var(--lilia-text-muted,#94a3b8)]">
              {counts.unresolved} open
            </span>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-0.5 p-0.5 rounded-lg bg-[var(--lilia-surface,#f1f5f9)]">
          {(
            [
              { key: "all", label: "All" },
              { key: "unresolved", label: "Open" },
              { key: "resolved", label: i18n.t("editor:comments.resolved") },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              data-testid="comments-filter-tab"
              data-filter={tab.key}
              aria-pressed={filterTab === tab.key}
              onClick={() => setFilterTab(tab.key)}
              className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                filterTab === tab.key
                  ? "bg-[var(--lilia-bg,#fff)] text-[var(--lilia-text,#334155)] shadow-sm"
                  : "text-[var(--lilia-text-muted,#94a3b8)] hover:text-[var(--lilia-text,#334155)]"
              }`}
            >
              {tab.label}
              {tab.key === "unresolved" && counts && counts.unresolved > 0 && (
                <span className="ml-1 text-[9px] opacity-60">
                  {counts.unresolved}
                </span>
              )}
              {tab.key === "resolved" && counts && counts.resolved > 0 && (
                <span className="ml-1 text-[9px] opacity-60">
                  {counts.resolved}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <span className="text-xs text-[var(--lilia-text-muted,#94a3b8)]">
              {i18n.t("editor:comments.loadingComments")}
            </span>
          </div>
        ) : !comments || comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <IconCircleDashed
              size={28}
              className="text-[var(--lilia-text-muted,#94a3b8)] opacity-40 mb-2"
            />
            <span className="text-xs text-[var(--lilia-text-muted,#94a3b8)]">
              {filterTab === "resolved"
                ? i18n.t("editor:comments.noResolvedComments")
                : filterTab === "unresolved"
                ? i18n.t("editor:comments.noOpenComments")
                : i18n.t("editor:comments.noCommentsYet")}
            </span>
            <span className="text-[10px] text-[var(--lilia-text-muted,#94a3b8)] opacity-60 mt-0.5">
              {i18n.t("editor:comments.addACommentBelowTo")}
            </span>
          </div>
        ) : (
          comments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              docId={documentId}
              blockLabel={
                comment.blockId
                  ? blockLabelMap.get(comment.blockId) ?? "Block"
                  : null
              }
              onScrollToBlock={handleScrollToBlock}
            />
          ))
        )}
      </div>

      {/* Add comment form */}
      <div className="border-t border-[var(--lilia-border,#e2e8f0)] px-3 py-3 bg-[var(--lilia-bg,#fff)]">
        <textarea
          data-testid="comments-add-textarea"
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={i18n.t("editor:comments.addACommentCtrlEnter")}
          rows={2}
          className="w-full px-2.5 py-2 text-xs rounded-md border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-bg,#fff)] text-[var(--lilia-text,#334155)] placeholder:text-[var(--lilia-text-muted,#94a3b8)] focus:outline-none focus:ring-1 focus:ring-[var(--lilia-primary,#0A76A4)] resize-none"
        />
        <div className="flex items-center gap-2 mt-2 min-w-0">
          <select
            data-testid="comments-block-select"
            value={newBlockId}
            onChange={(e) => setNewBlockId(e.target.value)}
            className="flex-1 min-w-0 px-2 py-1.5 text-[11px] rounded-md border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-bg,#fff)] text-[var(--lilia-text,#334155)] focus:outline-none focus:ring-1 focus:ring-[var(--lilia-primary,#0A76A4)] truncate"
          >
            <option value="">{i18n.t("editor:comments.generalNoBlock")}</option>
            {blocks?.map((block) => (
              <option key={block.id} value={block.id}>
                {blockLabelMap.get(block.id) || block.type}
              </option>
            ))}
          </select>
          <button
            data-testid="comments-add-submit"
            onClick={handleSubmit}
            disabled={!newContent.trim() || createComment.isPending}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--lilia-primary,#0A76A4)] text-[color:var(--lilia-on-primary)] text-xs font-medium disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            <IconSend size={13} />
            {i18n.t("editor:comments.send")}
          </button>
        </div>
      </div>
    </div>
  );
}
